<template>
  <v-container grid-list-xs class="pa-2">
    <v-layout row wrap>
       <v-flex :class="toolClass">
         <v-btn icon @click="showUploadDialog" :title="$t('upload-image')" v-if="!locked">
            <v-icon>attach_file</v-icon>
          </v-btn>
          <v-btn icon @click="showCamDialog" v-if="allowCamInput" :title="$t('capture-image')">
            <v-icon>camera_alt</v-icon>
          </v-btn>
        <v-btn icon :title="$t('add-note')" v-if="!locked" @click="addNote">
          <v-icon>note_add</v-icon>
        </v-btn>
        <v-chip small label outline :color="chipColor">IDX {{ index }}</v-chip>
      </v-flex>
      <v-flex :class="labelClass">
        {{ translatedLabel }}
      </v-flex>
      <v-flex xs12 order-xs3 v-if="translatedDescription">
        <p><v-icon small>info</v-icon> {{ translatedDescription }}</p>
      </v-flex>
    </v-layout>

    <!-- Picture editor @todo: refactor to component -->
    <v-layout row wrap>
      <v-flex v-for="id in imageIds" :key="id" class="xs12 sm6 md4 lg3 xl2">
        <v-img contain
              :src="images[id]['medium']"
              @click.stop="showDialogImage(id)"
              class="outline"
              height="200">
          <v-layout class="stripe">
            <v-flex class="pa-3">
              {{ getImageFilename(id) }}
            </v-flex>
            <v-flex text-right v-if="!locked">
              <v-btn text icon @click.stop="removeImage(id)" color="red darken-2" :title="$t('remove')">
                <v-icon>delete_forever</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-img>
      </v-flex>
    </v-layout>

    <!-- Note -->
    <v-layout row wrap>
      <v-flex xs12 order-xs4 :style="noteStyle">
        <v-textarea
          v-model="note"
          :label="$t('note')"
          :disabled="locked"
          rows="1"
          auto-grow
          clearable
        ></v-textarea>
      </v-flex>
    </v-layout>

    <!-- Camera capture modal -->
    <v-dialog v-model="camDialog" width="640">
      <v-card align-center>
        <video ref="cam"></video>
        <v-card-actions>
          <v-btn icon color="primary" @click="saveCamImage" :loading="uploading" :disabled="uploading">
            <v-icon color="white">camera</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn icon color="primary" @click="hideCamDialog">
            <v-icon color="white">clear</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- File upload dialog -->
    <v-dialog v-model="uploadDialog" width="500">
      <v-card justify-center>
        <form enctype="multipart/form-data" class="pa-3">
          <input ref="fileInput" name="pictureUploadInput" type="file" accept="image/*" class="outline pa-1"/>
        </form>
        <v-divider></v-divider>
        <v-card-actions text-right>
          <v-btn color="primary" @click="uploadImage" :loading="uploading" :disabled="uploading">
            {{ $t('upload') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn icon color="primary" @click="hideUploadDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog viever -->
    <v-dialog v-model="imageDialog">
      <v-card>
        <v-img
          v-if="images[dialogImage]"
          :src="images[dialogImage]['orig']"
          @click="hideDialogImage()"
          class="pointer"
          contain
        />
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
  .v-image:hover {
    cursor: pointer;
  }

  .stripe {
    background: rgba(255, 255, 255, 0.33);
  }
</style>

<script>
import validationMixin from './checklist-input-validation-mixin.es6'
import translationMixin from './checklist-translation-mixin.es6'
import noteMixin from './checklist-note-mixin.es6'

export default {
  name: 'checklist-input-image',
  mixins: [validationMixin, translationMixin, noteMixin],
  props: {
    index: Number,
    label: String,
    description: String,
    validations: Array,
    default: Object
  },
  created () {
    const { value, note } = this.default
    if (Array.isArray(value) && value.length > 0) {
      this.items = value
      this.loadImages()
    }
    // set user note
    if ((/string/).test(typeof note)) {
      this.note = note
    }
  },
  data () {
    return {
      items: [],
      uploadDialog: false,
      camDialog: false,
      camStream: null,
      imageDialog: false,
      dialogImage: null,
      uploading: false
    }
  },
  computed: {
    value () {
      return this.items
    },
    valid () {
      if (this.items.length === 0) {
        return true
      }

      if (this.validations.length === 0) {
        return true
      }

      return this.validate(this.items.length).valid
    },
    images () {
      return this.$store.state.files
    },
    imageIds () {
      return this.items.filter(id => this.images[id] && this.images[id]['medium'])
    },
    allowCamInput () {
      return !!navigator.mediaDevices.getUserMedia && !this.locked
    },
    isMobile () {
      return this.$store.state.userAgent.isMobile
    },
    locked () {
      return this.$store.getters['checklist/isLocked']
    },
    toolClass () {
      const { breakpoint } = this.$vuetify
      return {
        'text-right grey--text text--darken-2': true,
        'xs12': breakpoint.xs,
        'sm6': breakpoint.smAndUp,
        'order-xs1': breakpoint.xs,
        'order-sm2': breakpoint.smAndUp
      }
    },
    labelClass () {
      const { breakpoint } = this.$vuetify
      return {
        'subtitle-1 mt-3 pt-1': true,
        'xs12': breakpoint.xs,
        'sm6': breakpoint.smAndUp,
        'order-xs2': breakpoint.xs,
        'order-sm1': breakpoint.smAndUp
      }
    }
  },
  methods: {
    hideDialogImage (id) {
      this.imageDialog = false
    },

    async showDialogImage (id) {
      await this.getImageSource(id, '') // orig
      this.dialogImage = id
      this.imageDialog = true
    },

    async uploadImage () {
      const fileInput = this.$refs.fileInput

      if (!fileInput.files.length) {
        return // no file input
      }

      const file = fileInput.files[0]
      const data = new FormData()
      data.append('file', file)

      this.$emit('uploadImage', {
        data
      })

      this.uploading = true
    },

    async removeImage (id) {
      this.$emit('removeImage', {
        id
      })
    },

    async loadImages () {
      try {
        await Promise.all(this.items.map((id) => this.getImageSource(id)))
      } catch (error) {
        console.error(error)
      }
    },

    async getImageSource (id, size = 'medium') {
      try {
        return this.$store.dispatch('checklist/fetchImage', {
          id,
          requiredSize: size
        })
      } catch (error) {
        return Promise.reject(error)
      }
    },

    getImageFilename (id) {
      const file = this.$store.state.items.files.filter((f) => f.id === id).shift()
      return file ? file.path : ''
    },

    showUploadDialog () {
      this.uploadDialog = true
    },

    hideUploadDialog () {
      this.uploadDialog = false
    },

    showCamDialog () {
      const camera = this.$refs.cam
      const constraints = this.isMobile
        ? { video: { facingMode: { ideal: 'environment' }, audio: false } }
        : { video: true, audio: false }
      navigator.mediaDevices.getUserMedia(constraints)
        .then((stream) => {
          camera.srcObject = stream
          camera.play()

          this.camStream = stream
          this.camDialog = true
        })
        .catch(console.error)
    },

    hideCamDialog () {
      if (this.camStream) {
        this.camStream.getTracks().forEach((track) => track.stop())
        this.camStream = null
      }
      this.camDialog = false
    },

    saveCamImage () {
      const camera = this.$refs.cam
      const snapshot = document.createElement('canvas')
      snapshot.width = camera.videoWidth
      snapshot.height = camera.videoHeight

      const context = snapshot.getContext('2d')
      context.drawImage(camera, 0, 0)
      snapshot.toBlob((blob) => {
        const file = new File([blob], 'camera.png')
        const data = new FormData()
        data.append('file', file)

        this.$emit('uploadImage', {
          data
        })

        this.uploading = true
      })
    }
  },
  watch: {
    default: {
      handler ({ value, note }) {
        if (Array.isArray(value) && this.items.length !== value.length) {
          this.items = value
          this.uploading = false
          this.loadImages()
        }
        if (note !== null) {
          this.note = note
        }
      },
      deep: true
    }
  }
}
</script>
